import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Bell, MailOpen, Search, ExternalLink } from "lucide-react";

const TYPE_META = {
  "تقييم":   { key: "eval",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "مهمة":    { key: "task",       cls: "bg-teal-50 text-teal-700 border-teal-200" },
  "رسالة":   { key: "message",    cls: "bg-sky-50 text-sky-700 border-sky-200" },
  "جلسة":    { key: "session",    cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  "تسكين":   { key: "placement",  cls: "bg-purple-50 text-purple-700 border-purple-200" },
  "تعليق":   { key: "comment",    cls: "bg-cream-100 text-cream-800 border-cream-200" },
  "شكوى":    { key: "complaint",  cls: "bg-rose-50 text-rose-700 border-rose-200" },
  "شهادة":   { key: "certificate", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  "فرصة":    { key: "opportunity", cls: "bg-teal-50 text-teal-800 border-teal-200" },
  "تقرير":   { key: "report",     cls: "bg-slate-50 text-slate-700 border-slate-200" },
  "نشاط":    { key: "activity",   cls: "bg-sky-50 text-sky-800 border-sky-200" },
};

function classify(title) {
  const t = (title || "").toLowerCase();
  for (const [key, meta] of Object.entries(TYPE_META)) {
    if (t.includes(key)) return { label: key, cls: meta.cls, key: meta.key };
  }
  return { label: "عام", cls: "bg-slate-50 text-slate-600 border-slate-200", key: "general" };
}

const fmt = (iso) => { try { return new Date(iso).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" }); } catch { return "—"; } };

export default function StudentNotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const markAllRead = async () => {
    const unread = items.filter((n) => !n.read);
    await Promise.all(unread.map((n) => api.post(`/notifications/${n.id}/read`).catch(() => {})));
    toast.success(`تم تعيين ${unread.length} تنبيهات كمقروءة`);
    load();
  };

  const enriched = useMemo(() => items.map((n) => ({ ...n, _type: classify(n.title) })), [items]);
  const filtered = enriched.filter((n) => {
    if (filter !== "all" && n._type.key !== filter) return false;
    if (q && !`${n.title} ${n.body}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const counts = useMemo(() => {
    const c = { all: items.length, unread: items.filter((n) => !n.read).length };
    enriched.forEach((n) => { c[n._type.key] = (c[n._type.key] || 0) + 1; });
    return c;
  }, [enriched, items]);

  const filters = [
    { value: "all", label: "الكل", count: counts.all },
    ...Object.entries(TYPE_META)
      .map(([label, m]) => ({ value: m.key, label, count: counts[m.key] || 0 }))
      .filter((f) => f.count > 0),
  ];

  return (
    <DashboardLayout>
      <div data-testid="student-notifications">
        <div className="mb-6 flex flex-wrap justify-between items-start gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">مركز التنبيهات</div>
            <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">تنبيهاتي</h1>
            <p className="mt-2 text-slate-600 max-w-2xl">مراجعة كل التنبيهات مصنّفة حسب النوع (مهام، جلسات، تقييمات، رسائل، فرص...). اضغط على أي تنبيه لتعيينه كمقروء أو انتقل لصفحته.</p>
          </div>
          {counts.unread > 0 && (
            <button onClick={markAllRead} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="mark-all-read">
              <MailOpen className="h-4 w-4" /> تعيين الكل كمقروء ({counts.unread})
            </button>
          )}
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="notif-filters">
          {filters.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${filter === f.value ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-600 border-cream-200 hover:text-slate-900"}`} data-testid={`filter-${f.value}`}>
              {f.label} <span className="arabic-numerals opacity-70">·{f.count}</span>
            </button>
          ))}
        </div>

        <div className="mb-5 p-3 bg-white rounded-2xl border border-cream-200 flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث في التنبيهات..." className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="notif-search" />
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">
            {items.length === 0 ? "لا توجد تنبيهات." : "لا تنبيهات في هذا التصفية."}
          </div>
        ) : (
          <div className="space-y-2" data-testid="notif-list">
            {filtered.map((n) => (
              <div key={n.id} onClick={() => !n.read && markRead(n.id)} className={`p-4 rounded-2xl border card-shadow flex items-start gap-3 cursor-pointer transition-all ${n.read ? "bg-white border-cream-200" : "bg-teal-50/30 border-teal-100 hover:bg-teal-50/60"}`} data-testid={`notif-${n.id}`}>
                <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 border ${n._type.cls}`}>
                  <Bell className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-bold text-slate-900 text-sm">{n.title}</div>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${n._type.cls}`}>{n._type.label}</span>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-teal-600" title="غير مقروءة" />}
                  </div>
                  {n.body && <div className="text-sm text-slate-700 mt-1 whitespace-pre-line">{n.body}</div>}
                  <div className="text-[11px] text-slate-500 mt-1 arabic-numerals">{fmt(n.created_at)}</div>
                </div>
                {(n.resolved_link || n.link) && (
                  <Link to={n.resolved_link || n.link} onClick={(e) => { e.stopPropagation(); if (!n.read) markRead(n.id); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-teal-700 hover:bg-teal-50 text-xs font-bold shrink-0" data-testid={`open-${n.id}`}>
                    فتح <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
